/**
 * 複数グラフスロット（ResizableWrapper で可変幅表示するグラフ群）の
 * レイアウト高さ計算を各レポートで共有するためのユーティリティ。
 *
 * 機種集計・機種別推移・期間推移・SIS機種レポート・チェーン店/店舗レポートなど、
 * 同じ「複数スロット＋ドラッグリサイズ」構造の画面はこのモジュールに委譲する。
 * 新しい画面を追加するときは、画面固有の px 定数を渡して各 resolve 関数を呼ぶだけでよい。
 */

/** 表示本数ごとの「1本あたりグラフ高さ(px)」。画面ごとに下限の厚みが異なる */
export type GraphSlotChartHeights = {
  /** 1本表示時 */
  single: number;
  /** 2本表示時 */
  double: number;
  /** 3本表示時 */
  triple: number;
  /** 4本以上表示時 */
  overflow: number;
};

/** 機種別推移・期間推移・SIS で共通の標準値（360/200/140/120） */
export const DEFAULT_GRAPH_SLOT_CHART_HEIGHTS: GraphSlotChartHeights = {
  single: 360,
  double: 200,
  triple: 140,
  overflow: 120,
};

/**
 * 表示本数（スロット数）に応じた1本あたりグラフ高さ(px)を返す。
 * heights を省略すると標準値（{@link DEFAULT_GRAPH_SLOT_CHART_HEIGHTS}）を使う。
 */
export const resolveGraphSlotChartHeight = (
  graphCount: number,
  heights: GraphSlotChartHeights = DEFAULT_GRAPH_SLOT_CHART_HEIGHTS
): number => {
  if (graphCount <= 1) {
    return heights.single;
  }
  if (graphCount === 2) {
    return heights.double;
  }
  if (graphCount === 3) {
    return heights.triple;
  }
  return heights.overflow;
};

/** ResizableWrapper が収めるべき全体高さ(px)を計算するための画面別構成値 */
export type GraphSlotContainerHeightConfig = {
  /** タイトル・枠線など、グラフ本体以外の外周固定高さ */
  chromeHeight: number;
  /** タイトル行の高さ */
  titleHeight: number;
  /** 共通条件セレクタ行の高さ */
  controlsHeight: number;
  /** 各スロットの項目セレクト行の高さ */
  slotControlsHeight: number;
  /** スロット間の余白 */
  slotGap: number;
  /** フッター（グラフ追加・閉じるボタン）の高さ */
  footerHeight: number;
  /** 複数グラフ時に上部へ出す共有凡例の高さ。共有凡例が無い画面は省略 */
  sharedLegendHeight?: number;
  /** 表示本数 → 1本あたり高さ の解決関数 */
  resolveChartHeight: (count: number) => number;
};

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ(px)を返す。
 * グラフ本数に応じて領域を広げ、共有凡例の有無（options.hasMultipleGraphs）も加味する。
 *
 * gap は graphContent（gap 付き flex column）の実レイアウトに合わせ、
 * 「子要素数 - 1」本だけ計上する。子要素は各スロット＋共有凡例(あれば)。
 */
export const resolveGraphSlotContainerHeight = (
  slotCount: number,
  config: GraphSlotContainerHeightConfig,
  options?: { hasMultipleGraphs?: boolean }
): number => {
  const count = Math.max(slotCount, 1);
  const chartHeight = config.resolveChartHeight(count);
  // 共有凡例は複数グラフ時のみ、かつ画面が凡例を持つ（sharedLegendHeight 指定）場合だけ表示
  const hasSharedLegend =
    Boolean(options?.hasMultipleGraphs) && config.sharedLegendHeight != null;
  const sharedLegendHeight = hasSharedLegend
    ? config.sharedLegendHeight ?? 0
    : 0;
  // gap は隣接する子要素の「間」にのみ入るため本数は（子要素数 - 1）
  const childCount = count + (hasSharedLegend ? 1 : 0);
  const gapTotal = Math.max(childCount - 1, 0) * config.slotGap;
  const graphsHeight =
    count * (chartHeight + config.slotControlsHeight) +
    gapTotal +
    sharedLegendHeight;

  return (
    config.chromeHeight +
    config.titleHeight +
    config.controlsHeight +
    graphsHeight +
    config.footerHeight
  );
};

/** ResizableWrapper の下限高さ(px)。全画面共通で最低これだけは確保する */
export const GRAPH_SLOT_MIN_RESIZABLE_HEIGHT = 625;
/** ResizableWrapper の上限高さ(px)。全画面共通の基準上限 */
export const GRAPH_SLOT_MAX_RESIZABLE_HEIGHT = 1400;
/** リサイズハンドルのツールチップ文言（全画面共通） */
export const GRAPH_SLOT_RESIZABLE_TOOLTIP = 'ドラッグして表示領域を調整';

/**
 * container 高さから ResizableWrapper へ渡す default/min/max 高さ(px)を導出する。
 * default は min と同値にして、初期表示で全グラフが収まる高さから始める。
 */
export const resolveGraphSlotResizableHeights = (
  containerHeight: number
): { defaultHeight: number; minHeight: number; maxHeight: number } => {
  const minHeight = Math.max(GRAPH_SLOT_MIN_RESIZABLE_HEIGHT, containerHeight);
  return {
    defaultHeight: minHeight,
    minHeight,
    maxHeight: Math.max(GRAPH_SLOT_MAX_RESIZABLE_HEIGHT, containerHeight),
  };
};
